import { useState } from 'react'
import { toast } from 'sonner'
import { HugeiconsIcon } from "@hugeicons/react"
import { Copy01Icon, Tick01Icon } from "@hugeicons/core-free-icons"
import { Button } from '@/components/ui/button'
import { ToolSegmented } from '@/components/tools/ToolKit'
import { getTrackingSnippet } from '@/lib/utils/trackingSnippet'
import { GtmInstructions } from '@/components/onboarding/GtmInstructions'

type InstallMethod = 'manual' | 'gtm'

interface EmbedCodeDisplayProps {
  scriptId: string
  onCopy?: () => void
  onIdCopied?: () => void
}

export default function EmbedCodeDisplay({ scriptId, onCopy, onIdCopied }: EmbedCodeDisplayProps) {
  const [installMethod, setInstallMethod] = useState<InstallMethod>('manual')
  const [copied, setCopied] = useState(false)
  const [everCopied, setEverCopied] = useState(false)

  const embedCode = getTrackingSnippet(scriptId)

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(embedCode)
      setCopied(true)
      setEverCopied(true)
      onCopy?.()
      toast.success('Code copied to clipboard')
      setTimeout(() => setCopied(false), 2500)
    } catch {
      toast.error('Failed to copy code')
    }
  }

  return (
    <section className="surface-raised flex flex-col overflow-clip">
      <div className="flex flex-col gap-3 px-[18px] pb-3 pt-4 sm:flex-row sm:items-center sm:gap-2.5">
        <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
          <h2 className="text-sm font-semibold leading-[18px] text-foreground">Tracking code</h2>
          <p className="text-xs leading-4 text-foreground/60">Copy the snippet or install through Google Tag Manager</p>
        </div>
        <ToolSegmented
          label="Install method"
          options={[
            { value: 'manual', label: 'Manual' },
            { value: 'gtm', label: 'Google Tag Manager' },
          ]}
          value={installMethod}
          onChange={setInstallMethod}
        />
      </div>

      <div className="flex flex-col gap-3 px-[18px] pb-[18px]">
        {installMethod === 'manual' ? (
          <>
            <p className="text-xs leading-4 text-foreground/60">Paste into your site&apos;s head, before the closing tag.</p>
            <div className="flex items-center gap-2.5 rounded-lg border border-border bg-muted/50 py-3 pl-4 pr-3">
              <code className="min-w-0 flex-1 overflow-x-auto whitespace-pre font-mono text-xs leading-[18px] text-foreground scrollbar-hide">
                {embedCode}
              </code>
              <Button
                variant="outline"
                onClick={copyToClipboard}
                className="h-7 shrink-0 gap-1.5 rounded-md px-2.5 text-xs font-medium shadow-none"
              >
                <HugeiconsIcon icon={copied ? Tick01Icon : Copy01Icon} size={12} />
                {copied ? 'Copied' : 'Copy code'}
              </Button>
            </div>
            <div className="flex items-center gap-2.5">
              <Button
                onClick={copyToClipboard}
                variant={everCopied ? 'outline' : 'default'}
                className="h-[34px] gap-[7px] rounded-lg px-3.5 text-[13px] font-medium shadow-none"
              >
                <HugeiconsIcon icon={copied ? Tick01Icon : Copy01Icon} size={13} />
                {copied ? 'Copied to clipboard' : 'Copy tracking code'}
              </Button>
              <span className="text-xs leading-4 text-foreground/60">Site ID {scriptId}</span>
            </div>
          </>
        ) : (
          <GtmInstructions scriptId={scriptId} onIdCopied={onIdCopied} />
        )}
      </div>
    </section>
  )
}
